import type { PersistedSnapshotV1, PersistedTableName } from "../persistence/records";
import { createCompletedCheckIn } from "../../domain/check-in/check-in";
import {
  FOOD_BARCODE_MAX_LENGTH,
  FOOD_BRAND_MAX_LENGTH,
  FOOD_ID_MAX_LENGTH,
  FOOD_MEASURE_ID_MAX_LENGTH,
  FOOD_MEASURE_LABEL_MAX_LENGTH,
  FOOD_MEASURE_UNITS,
  FOOD_NAME_MAX_LENGTH,
  FOOD_SERVING_NAME_MAX_LENGTH,
  validateFood,
  validateFoodBasis,
  validateFoodPortion,
} from "../../domain/foods/food";
import { validateGoalPhase } from "../../domain/goals/goal";
import { validateRecipe } from "../../domain/recipes/recipe";
import {
  NUTRIENT_KEY_MAX_LENGTH,
  NUTRIENT_NAME_MAX_LENGTH,
  validateNutrientProfile,
} from "../../domain/nutrition/nutrients";
import { createDailyNutrientTarget } from "../../domain/nutrition/nutrient-target";
import { validateWeekPlan } from "../../domain/planning/week-plan";
import { createWeightEntry } from "../../domain/weight/weight-entry";
import { createOnboardingWeightReference } from "../../domain/weight/onboarding-reference";
import {
  checkInFromRecord,
  foodFromRecord,
  goalFromRecord,
  nutrientTargetFromRecord,
  nutrientsFromRecord,
  onboardingReferenceFromRecord,
  recipeFromRecord,
  weekPlanFromRecord,
  weightFromRecord,
} from "../persistence/indexed-db/domain-mappers";
import {
  BACKUP_FORMAT,
  CURRENT_BACKUP_FORMAT_VERSION,
  MAX_BACKUP_BYTES,
  type BackupEnvelope,
} from "./backup-document";
import { migrateBackupPayload } from "./backup-migrations";
import { sha256Hex } from "./checksum";

const TABLES: PersistedTableName[] = [
  "profiles",
  "settings",
  "goalPhases",
  "nutrientGoals",
  "foods",
  "recipes",
  "diaryEntries",
  "weightEntries",
  "onboardingReferences",
  "weekPlans",
  "checkIns",
  "backupState",
  "metadata",
];
const FORMAT_1_TABLES = TABLES.filter((table) => table !== "onboardingReferences" && table !== "recipes");
const FORMAT_2_TABLES = TABLES.filter((table) => table !== "recipes");
const LOCAL_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HEX_SHA_256 = /^[a-f0-9]{64}$/;

export class InvalidBackupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidBackupError";
  }
}

export function parseBackupJson(text: string): unknown {
  const size = new TextEncoder().encode(text).byteLength;
  if (size > MAX_BACKUP_BYTES) {
    throw new InvalidBackupError(`Backup is too large (${size} bytes)`);
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new InvalidBackupError("Backup is not valid JSON");
  }
}

export async function validateBackupEnvelope(input: unknown): Promise<BackupEnvelope> {
  assertSafeJsonTree(input);
  const envelope = requireRecord(input, "backup");
  requireEqual(envelope.format, BACKUP_FORMAT, "format");
  const version = requireInteger(envelope.formatVersion, "formatVersion", 1);
  if (version > CURRENT_BACKUP_FORMAT_VERSION) {
    throw new InvalidBackupError("This backup was created by a newer version of the app");
  }
  requireString(envelope.backupId, "backupId", 1, 200);
  requireTimestamp(envelope.createdAt, "createdAt");
  requireString(envelope.appVersion, "appVersion", 1, 100);
  requireString(envelope.deviceId, "deviceId", 1, 200);
  requireInteger(envelope.revision, "revision", 0);
  requireEqual(envelope.checksumAlgorithm, "SHA-256", "checksumAlgorithm");
  const checksum = requireString(envelope.checksum, "checksum", 64, 64);
  if (!HEX_SHA_256.test(checksum)) throw new InvalidBackupError("checksum is malformed");

  const { checksum: _removed, ...unsigned } = envelope;
  const expected = await sha256Hex(unsigned);
  if (!constantTimeEqual(checksum, expected)) throw new InvalidBackupError("Backup checksum does not match");

  // Counts describe the signed payload in its own format. Checking only the
  // tables that existed in that version prevents a valid old backup from being
  // rejected merely because a later format added a table.
  validateRawRecordCounts(version, envelope.payload, envelope.recordCounts);

  let migrated: PersistedSnapshotV1;
  try {
    migrated = migrateBackupPayload(version, envelope.payload);
  } catch (error) {
    throw new InvalidBackupError(`Backup migration failed: ${error instanceof Error ? error.message : String(error)}`);
  }
  // Current application invariants apply only after the authenticated raw
  // payload has traversed its version-specific migration path.
  validateSnapshot(migrated);

  return envelope as unknown as BackupEnvelope;
}

function validateRawRecordCounts(version: number, payloadInput: unknown, countsInput: unknown): void {
  const payload = requireRecord(payloadInput, "payload");
  const counts = requireRecord(countsInput, "recordCounts");
  const requiredTables = version === 1 ? FORMAT_1_TABLES : version === 2 ? FORMAT_2_TABLES : TABLES;
  const tables = new Set<PersistedTableName>(requiredTables);

  // A format-1 writer from the transition period may already contain a newer
  // known table. If either side declares it, require and authenticate its count.
  for (const table of TABLES) {
    if (payload[table] !== undefined || counts[table] !== undefined) tables.add(table);
  }

  for (const table of tables) {
    const records = requireArray(payload[table], `payload.${table}`);
    const supplied = requireInteger(counts[table], `recordCounts.${table}`, 0);
    if (supplied !== records.length) {
      throw new InvalidBackupError(`recordCounts.${table} does not match the payload`);
    }
  }
}

export function validateSnapshot(input: unknown): PersistedSnapshotV1 {
  const payload = requireRecord(input, "payload");
  for (const table of TABLES) requireArray(payload[table], `payload.${table}`);

  const snapshot = payload as unknown as PersistedSnapshotV1;
  validateUnique(snapshot.profiles, "id", "profiles");
  validateUnique(snapshot.settings, "id", "settings");
  validateUnique(snapshot.goalPhases, "id", "goalPhases");
  validateUnique(snapshot.nutrientGoals, "id", "nutrientGoals");
  validateUnique(snapshot.foods, "id", "foods");
  validateUnique(snapshot.recipes, "id", "recipes");
  validateUnique(snapshot.diaryEntries, "id", "diaryEntries");
  validateUnique(snapshot.weightEntries, "date", "weightEntries");
  validateUnique(snapshot.onboardingReferences, "id", "onboardingReferences");
  validateUnique(snapshot.weekPlans, "id", "weekPlans");
  validateUnique(snapshot.checkIns, "id", "checkIns");
  validateUnique(snapshot.backupState, "id", "backupState");
  validateUnique(snapshot.metadata, "key", "metadata");

  snapshot.foods.forEach((food, index) => {
    const item = requireRecord(food, `foods[${index}]`);
    requireString(item.id, `foods[${index}].id`, 1, FOOD_ID_MAX_LENGTH);
    requireString(item.name, `foods[${index}].name`, 1, FOOD_NAME_MAX_LENGTH);
    if (item.brand !== undefined) requireString(item.brand, `foods[${index}].brand`, 1, FOOD_BRAND_MAX_LENGTH);
    if (item.barcode !== undefined) requireString(item.barcode, `foods[${index}].barcode`, 1, FOOD_BARCODE_MAX_LENGTH);
    validateFoodBasisRecord(item.basis, `foods[${index}].basis`);
    if (item.measures !== undefined) validateFoodMeasuresRecord(item.measures, `foods[${index}].measures`);
    validateNutrition(item.nutrition, `foods[${index}].nutrition`);
  });
  snapshot.recipes.forEach((recipe, index) => {
    const path = `recipes[${index}]`;
    const item = requireRecord(recipe, path);
    requireString(item.id, `${path}.id`, 1, FOOD_ID_MAX_LENGTH);
    requireString(item.name, `${path}.name`, 1, FOOD_NAME_MAX_LENGTH);
    requireString(item.servingName, `${path}.servingName`, 1, FOOD_SERVING_NAME_MAX_LENGTH);
    requireFiniteNumber(item.servingCount, `${path}.servingCount`, Number.MIN_VALUE, Number.MAX_VALUE);
    const ingredients = requireArray(item.ingredients, `${path}.ingredients`);
    ingredients.forEach((ingredientInput, ingredientIndex) => {
      const ingredientPath = `${path}.ingredients[${ingredientIndex}]`;
      const ingredient = requireRecord(ingredientInput, ingredientPath);
      requireString(ingredient.foodId, `${ingredientPath}.foodId`, 1, FOOD_ID_MAX_LENGTH);
      requireString(ingredient.name, `${ingredientPath}.name`, 1, FOOD_NAME_MAX_LENGTH);
      if (ingredient.brand !== undefined) {
        requireString(ingredient.brand, `${ingredientPath}.brand`, 1, FOOD_BRAND_MAX_LENGTH);
      }
      const basis = validateFoodBasisRecord(ingredient.basis, `${ingredientPath}.basis`);
      const portion = validateFoodPortionRecord(ingredient.portion, `${ingredientPath}.portion`);
      if (portion.unit !== basis.unit) {
        throw new InvalidBackupError(`${ingredientPath}.portion.unit does not match its food basis`);
      }
      validateNutrition(ingredient.nutrition, `${ingredientPath}.nutrition`);
    });
  });
  snapshot.diaryEntries.forEach((entry, index) => {
    const item = requireRecord(entry, `diaryEntries[${index}]`);
    requireLocalDate(item.date, `diaryEntries[${index}].date`);
    const food = requireRecord(item.food, `diaryEntries[${index}].food`);
    requireString(food.foodId, `diaryEntries[${index}].food.foodId`, 1, FOOD_ID_MAX_LENGTH);
    requireString(food.name, `diaryEntries[${index}].food.name`, 1, FOOD_NAME_MAX_LENGTH);
    if (food.brand !== undefined) requireString(food.brand, `diaryEntries[${index}].food.brand`, 1, FOOD_BRAND_MAX_LENGTH);
    if (food.barcode !== undefined) requireString(food.barcode, `diaryEntries[${index}].food.barcode`, 1, FOOD_BARCODE_MAX_LENGTH);
    const basis = validateFoodBasisRecord(food.basis, `diaryEntries[${index}].food.basis`);
    if (food.measures !== undefined) {
      validateFoodMeasuresRecord(food.measures, `diaryEntries[${index}].food.measures`);
    }
    const portion = validateFoodPortionRecord(food.portion, `diaryEntries[${index}].food.portion`);
    if (portion.unit !== basis.unit) {
      throw new InvalidBackupError(`diaryEntries[${index}].food.portion.unit does not match its food basis`);
    }
    validateNutrition(food.nutrition, `diaryEntries[${index}].food.nutrition`);
  });
  snapshot.weightEntries.forEach((entry, index) => {
    const item = requireRecord(entry, `weightEntries[${index}]`);
    requireLocalDate(item.date, `weightEntries[${index}].date`);
    requireInteger(item.weightGrams, `weightEntries[${index}].weightGrams`, 1, 1_000_000);
  });
  snapshot.onboardingReferences.forEach((reference, index) => {
    const path = `onboardingReferences[${index}]`;
    const item = requireRecord(reference, path);
    requireEqual(item.id, "onboarding", `${path}.id`);
    requireLocalDate(item.date, `${path}.date`);
    requireInteger(item.weightGrams, `${path}.weightGrams`, 1, 1_000_000);
    requireString(item.trendPolicyVersion, `${path}.trendPolicyVersion`, 1, 200);
    requireTimestamp(item.createdAt, `${path}.createdAt`);
    const samples = requireArray(item.samples, `${path}.samples`);
    if (samples.length !== 1) throw new InvalidBackupError(`${path}.samples must contain the immutable onboarding measurement`);
    const sample = requireRecord(samples[0], `${path}.samples[0]`);
    requireLocalDate(sample.date, `${path}.samples[0].date`);
    requireInteger(sample.weightGrams, `${path}.samples[0].weightGrams`, 1, 1_000_000);
    if (sample.date !== item.date || sample.weightGrams !== item.weightGrams) {
      throw new InvalidBackupError(`${path}.samples does not match its reference weight`);
    }
  });
  snapshot.weekPlans.forEach((plan, index) => {
    const item = requireRecord(plan, `weekPlans[${index}]`);
    requireLocalDate(item.startDate, `weekPlans[${index}].startDate`);
    requireLocalDate(item.endDate, `weekPlans[${index}].endDate`);
    if ((item.startDate as string) > (item.endDate as string)) {
      throw new InvalidBackupError(`weekPlans[${index}] has an inverted date range`);
    }
    const allocations = requireArray(item.allocations, `weekPlans[${index}].allocations`);
    if (allocations.length !== 7) throw new InvalidBackupError(`weekPlans[${index}] must contain seven allocations`);
    for (const [allocationIndex, allocationInput] of allocations.entries()) {
      const allocation = requireRecord(allocationInput, `weekPlans[${index}].allocations[${allocationIndex}]`);
      requireLocalDate(allocation.date, `weekPlans[${index}].allocations[${allocationIndex}].date`);
      requireInteger(allocation.calories, `weekPlans[${index}].allocations[${allocationIndex}].calories`, 0);
      if (allocation.isLocked !== undefined && typeof allocation.isLocked !== "boolean") {
        throw new InvalidBackupError(`weekPlans[${index}].allocations[${allocationIndex}].isLocked must be a boolean`);
      }
    }
  });

  const planIds = new Set(snapshot.weekPlans.map((plan) => plan.id));
  snapshot.checkIns.forEach((checkIn, index) => {
    const item = requireRecord(checkIn, `checkIns[${index}]`);
    requireLocalDate(item.dueDate, `checkIns[${index}].dueDate`);
    requireLocalDate(item.completedDate, `checkIns[${index}].completedDate`);
    requireTimestamp(item.completedAt, `checkIns[${index}].completedAt`);
    const planId = requireString(item.weekPlanId, `checkIns[${index}].weekPlanId`, 1, 200);
    if (!planIds.has(planId)) throw new InvalidBackupError(`checkIns[${index}] refers to a missing week plan`);
    const nextPlanId = requireString(item.nextWeekPlanId, `checkIns[${index}].nextWeekPlanId`, 1, 200);
    if (!planIds.has(nextPlanId)) throw new InvalidBackupError(`checkIns[${index}] refers to a missing next week plan`);
    const questionnaire = requireRecord(item.questionnaire, `checkIns[${index}].questionnaire`);
    if (typeof questionnaire.loggedEverything !== "boolean") {
      throw new InvalidBackupError(`checkIns[${index}].questionnaire.loggedEverything is invalid`);
    }
    const compliance = questionnaire.calorieCompliancePercent;
    if (typeof compliance !== "number" || !Number.isFinite(compliance) || compliance < 0 || compliance > 100) {
      throw new InvalidBackupError(`checkIns[${index}].questionnaire.calorieCompliancePercent is invalid`);
    }
    const recommendation = requireRecord(item.recommendation, `checkIns[${index}].recommendation`);
    requireString(recommendation.policyVersion, `checkIns[${index}].recommendation.policyVersion`, 1, 200);
    requireInteger(recommendation.previousBaselineCalories, `checkIns[${index}].recommendation.previousBaselineCalories`, 1);
    requireInteger(recommendation.recommendedBaselineCalories, `checkIns[${index}].recommendation.recommendedBaselineCalories`, 1);
    requireFiniteNumber(recommendation.dailyAdjustmentCalories, `checkIns[${index}].recommendation.dailyAdjustmentCalories`, -10_000, 10_000);
    requireFiniteNumber(recommendation.uncappedDailyAdjustmentCalories, `checkIns[${index}].recommendation.uncappedDailyAdjustmentCalories`, -1_000_000, 1_000_000);
    requireString(recommendation.reason, `checkIns[${index}].recommendation.reason`, 1, 100);
    if (recommendation.previousTrendWeightKilograms !== undefined) {
      requireFiniteNumber(recommendation.previousTrendWeightKilograms, `checkIns[${index}].recommendation.previousTrendWeightKilograms`, Number.EPSILON, 1_000);
    }
    if (recommendation.currentTrendWeightKilograms !== undefined) {
      requireFiniteNumber(recommendation.currentTrendWeightKilograms, `checkIns[${index}].recommendation.currentTrendWeightKilograms`, Number.EPSILON, 1_000);
    }
    requireInteger(item.acceptedBaselineCalories, `checkIns[${index}].acceptedBaselineCalories`, 1);
    if (typeof item.baselineWasOverridden !== "boolean") {
      throw new InvalidBackupError(`checkIns[${index}].baselineWasOverridden is invalid`);
    }
    if (item.previousTrend !== undefined) validateTrendSnapshotRecord(item.previousTrend, `checkIns[${index}].previousTrend`);
    if (item.currentTrend !== undefined) validateTrendSnapshotRecord(item.currentTrend, `checkIns[${index}].currentTrend`);
    const protein = requireRecord(item.proteinDecision, `checkIns[${index}].proteinDecision`);
    requireString(protein.policyVersion, `checkIns[${index}].proteinDecision.policyVersion`, 1, 200);
    requireInteger(protein.proposedDailyGrams, `checkIns[${index}].proteinDecision.proposedDailyGrams`, 1, 2_000);
    requireInteger(protein.acceptedDailyGrams, `checkIns[${index}].proteinDecision.acceptedDailyGrams`, 1, 2_000);
    if (typeof protein.wasOverridden !== "boolean") {
      throw new InvalidBackupError(`checkIns[${index}].proteinDecision.wasOverridden is invalid`);
    }
    requireFiniteNumber(protein.gramsPerKilogram, `checkIns[${index}].proteinDecision.gramsPerKilogram`, Number.EPSILON, 20);
    if (protein.referenceWeightGrams !== undefined) {
      requireInteger(protein.referenceWeightGrams, `checkIns[${index}].proteinDecision.referenceWeightGrams`, 1, 1_000_000);
    }
    if (protein.referenceDate !== undefined) requireLocalDate(protein.referenceDate, `checkIns[${index}].proteinDecision.referenceDate`);
    if (protein.source !== "current_weight_trend" && protein.source !== "previous_target") {
      throw new InvalidBackupError(`checkIns[${index}].proteinDecision.source is invalid`);
    }
  });

  if (snapshot.profiles.length > 1 || snapshot.settings.length > 1 || snapshot.backupState.length > 1 || snapshot.onboardingReferences.length > 1) {
    throw new InvalidBackupError("Backup contains duplicate singleton records");
  }
  if (snapshot.profiles[0]?.id !== undefined && snapshot.profiles[0].id !== "primary") {
    throw new InvalidBackupError("Profile identity is invalid");
  }
  if (snapshot.settings[0]?.id !== undefined && snapshot.settings[0].id !== "settings") {
    throw new InvalidBackupError("Settings identity is invalid");
  }
  const settings = snapshot.settings[0];
  if (settings) {
    if (!["kg", "lb"].includes(settings.weightUnit)) throw new InvalidBackupError("Weight unit is invalid");
    requireInteger(settings.nominatedCheckInWeekday, "settings.nominatedCheckInWeekday", 0, 6);
    if (typeof settings.googleDriveBackupEnabled !== "boolean") {
      throw new InvalidBackupError("Google Drive setting is invalid");
    }
  }
  const profile = snapshot.profiles[0];
  if (profile) {
    requireString(profile.homeTimeZone, "profile.homeTimeZone", 1, 100);
    try {
      new Intl.DateTimeFormat("en", { timeZone: profile.homeTimeZone }).format();
    } catch {
      throw new InvalidBackupError("Profile time zone is invalid");
    }
  }
  const backupState = snapshot.backupState[0];
  if (backupState) {
    if (backupState.id !== "google-drive") throw new InvalidBackupError("Backup state identity is invalid");
    requireString(backupState.deviceId, "backupState.deviceId", 1, 200);
    requireInteger(backupState.localRevision, "backupState.localRevision", 0);
    if (typeof backupState.pending !== "boolean") throw new InvalidBackupError("Backup pending state is invalid");
  }
  const goalIds = new Set(snapshot.goalPhases.map((goal) => goal.id));
  snapshot.goalPhases.forEach((goal, index) => requireLocalDate(goal.effectiveFrom, `goalPhases[${index}].effectiveFrom`));
  snapshot.nutrientGoals.forEach((goal, index) => {
    requireString(goal.nutrientKey, `nutrientGoals[${index}].nutrientKey`, 1, NUTRIENT_KEY_MAX_LENGTH);
    if (!["g", "mg", "mcg", "IU"].includes(goal.unit)) {
      throw new InvalidBackupError(`nutrientGoals[${index}].unit is unsupported`);
    }
    if (goal.name !== undefined) requireString(goal.name, `nutrientGoals[${index}].name`, 1, NUTRIENT_NAME_MAX_LENGTH);
  });
  for (const [index, plan] of snapshot.weekPlans.entries()) {
    if (!goalIds.has(plan.goalPhaseId)) throw new InvalidBackupError(`weekPlans[${index}] refers to a missing goal`);
  }
  if (snapshot.weekPlans.filter((plan) => plan.status === "active").length > 1) {
    throw new InvalidBackupError("Backup contains more than one active week plan");
  }

  try {
    snapshot.foods.forEach((food) => validateFood(foodFromRecord(food)));
    snapshot.recipes.forEach((recipe) => validateRecipe(recipeFromRecord(recipe)));
    snapshot.diaryEntries.forEach((entry) => {
      validateFoodBasis(entry.food.basis);
      validateFoodPortion(entry.food.portion, entry.food.basis, entry.food.measures);
      validateNutrientProfile(nutrientsFromRecord(entry.food.nutrition));
    });
    snapshot.weightEntries.forEach((weight) => createWeightEntry(weightFromRecord(weight)));
    snapshot.onboardingReferences.forEach((reference) => createOnboardingWeightReference({
      date: onboardingReferenceFromRecord(reference).date,
      weightKilograms: onboardingReferenceFromRecord(reference).weightKilograms,
      createdAt: onboardingReferenceFromRecord(reference).createdAt,
    }));
    snapshot.goalPhases.forEach((goal) => validateGoalPhase(goalFromRecord(goal)));
    snapshot.nutrientGoals.forEach((goal) => createDailyNutrientTarget(nutrientTargetFromRecord(goal)));
    snapshot.weekPlans.forEach((plan) => validateWeekPlan(weekPlanFromRecord(plan)));
    snapshot.checkIns.forEach((checkIn) => createCompletedCheckIn(checkInFromRecord(checkIn)));
  } catch (error) {
    throw new InvalidBackupError(`Backup violates an application invariant: ${error instanceof Error ? error.message : String(error)}`);
  }

  return snapshot;
}

function validateNutrition(input: unknown, path: string): void {
  const nutrition = requireRecord(input, path);
  for (const key of ["proteinMg", "carbohydrateMg", "fatMg"]) {
    requireInteger(nutrition[key], `${path}.${key}`, 0, 10_000_000_000);
  }
  if (nutrition.fibreMg !== undefined) requireInteger(nutrition.fibreMg, `${path}.fibreMg`, 0, 10_000_000_000);
  const micros = requireRecord(nutrition.micronutrients, `${path}.micronutrients`);
  for (const [key, value] of Object.entries(micros)) {
    if (!key || key.length > NUTRIENT_KEY_MAX_LENGTH) throw new InvalidBackupError(`${path} has an invalid nutrient key`);
    const nutrient = requireRecord(value, `${path}.micronutrients.${key}`);
    const amount = nutrient.amount;
    if (typeof amount !== "number" || !Number.isFinite(amount) || amount < 0) {
      throw new InvalidBackupError(`${path}.micronutrients.${key}.amount must be non-negative`);
    }
    if (!["g", "mg", "mcg", "IU"].includes(String(nutrient.unit))) {
      throw new InvalidBackupError(`${path}.micronutrients.${key}.unit is unsupported`);
    }
    if (nutrient.name !== undefined) {
      requireString(nutrient.name, `${path}.micronutrients.${key}.name`, 1, NUTRIENT_NAME_MAX_LENGTH);
    }
  }
}

function validateFoodBasisRecord(input: unknown, path: string): { amount: number; unit: "g" | "ml" | "serving" } {
  const basis = requireRecord(input, path);
  const amount = requireFiniteNumber(basis.amount, `${path}.amount`, Number.MIN_VALUE, Number.MAX_VALUE);
  const unit = requireString(basis.unit, `${path}.unit`, 1, 20);
  if (!(FOOD_MEASURE_UNITS as readonly string[]).includes(unit)) {
    throw new InvalidBackupError(`${path}.unit is unsupported`);
  }
  if (basis.servingName !== undefined) {
    requireString(basis.servingName, `${path}.servingName`, 1, FOOD_SERVING_NAME_MAX_LENGTH);
  }
  if (unit === "serving" && basis.servingName === undefined) {
    throw new InvalidBackupError(`${path}.servingName is required for a serving basis`);
  }
  return { amount, unit: unit as "g" | "ml" | "serving" };
}

function validateFoodMeasuresRecord(input: unknown, path: string): void {
  const measures = requireArray(input, path);
  const seen = new Set<string>();
  for (const [index, inputMeasure] of measures.entries()) {
    const measurePath = `${path}[${index}]`;
    const measure = requireRecord(inputMeasure, measurePath);
    const id = requireString(measure.id, `${measurePath}.id`, 1, FOOD_MEASURE_ID_MAX_LENGTH);
    if (seen.has(id)) throw new InvalidBackupError(`${path} contains duplicate measure id '${id}'`);
    seen.add(id);
    requireString(measure.label, `${measurePath}.label`, 1, FOOD_MEASURE_LABEL_MAX_LENGTH);
    requireFiniteNumber(measure.amount, `${measurePath}.amount`, Number.MIN_VALUE, Number.MAX_VALUE);
  }
}

function validateFoodPortionRecord(input: unknown, path: string): { amount: number; unit: "g" | "ml" | "serving" } {
  const portion = requireRecord(input, path);
  const amount = requireFiniteNumber(portion.amount, `${path}.amount`, Number.MIN_VALUE, Number.MAX_VALUE);
  const unit = requireString(portion.unit, `${path}.unit`, 1, 20);
  if (!(FOOD_MEASURE_UNITS as readonly string[]).includes(unit)) {
    throw new InvalidBackupError(`${path}.unit is unsupported`);
  }
  if (portion.display !== undefined) {
    const display = requireRecord(portion.display, `${path}.display`);
    requireFiniteNumber(display.amount, `${path}.display.amount`, Number.MIN_VALUE, Number.MAX_VALUE);
    requireString(display.measureId, `${path}.display.measureId`, 1, FOOD_MEASURE_ID_MAX_LENGTH);
    requireString(display.label, `${path}.display.label`, 1, FOOD_MEASURE_LABEL_MAX_LENGTH);
  }
  return { amount, unit: unit as "g" | "ml" | "serving" };
}

function validateTrendSnapshotRecord(input: unknown, path: string): void {
  const trend = requireRecord(input, path);
  requireString(trend.policyVersion, `${path}.policyVersion`, 1, 200);
  if (!["current_check_in", "previous_check_in", "onboarding_reference"].includes(String(trend.source))) {
    throw new InvalidBackupError(`${path}.source is invalid`);
  }
  const trendDate = requireLocalDate(trend.date, `${path}.date`);
  requireInteger(trend.averageWeightGrams, `${path}.averageWeightGrams`, 1, 1_000_000);
  const samples = requireArray(trend.samples, `${path}.samples`);
  if (samples.length < 1 || samples.length > 5) throw new InvalidBackupError(`${path}.samples must contain one to five weights`);
  const seen = new Set<string>();
  for (const [index, sampleInput] of samples.entries()) {
    const sample = requireRecord(sampleInput, `${path}.samples[${index}]`);
    const date = requireLocalDate(sample.date, `${path}.samples[${index}].date`);
    requireInteger(sample.weightGrams, `${path}.samples[${index}].weightGrams`, 1, 1_000_000);
    if (date > trendDate || seen.has(date)) throw new InvalidBackupError(`${path}.samples contains an invalid date`);
    seen.add(date);
  }
}

function validateUnique(items: unknown[], key: string, path: string): void {
  const seen = new Set<unknown>();
  items.forEach((item, index) => {
    const value = requireRecord(item, `${path}[${index}]`)[key];
    if (typeof value !== "string" || !value) throw new InvalidBackupError(`${path}[${index}].${key} is invalid`);
    if (seen.has(value)) throw new InvalidBackupError(`${path} contains duplicate ${key} '${value}'`);
    seen.add(value);
  });
}

function assertSafeJsonTree(value: unknown, depth = 0): void {
  if (depth > 100) throw new InvalidBackupError("Backup is nested too deeply");
  if (value === null || ["string", "boolean"].includes(typeof value)) return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new InvalidBackupError("Backup contains a non-finite number");
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => assertSafeJsonTree(item, depth + 1));
    return;
  }
  if (typeof value !== "object") throw new InvalidBackupError("Backup contains an unsupported value");
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (["__proto__", "prototype", "constructor"].includes(key)) {
      throw new InvalidBackupError("Backup contains an unsafe property name");
    }
    assertSafeJsonTree(child, depth + 1);
  }
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new InvalidBackupError(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new InvalidBackupError(`${path} must be an array`);
  if (value.length > 1_000_000) throw new InvalidBackupError(`${path} contains too many records`);
  return value;
}

function requireString(value: unknown, path: string, min: number, max: number): string {
  if (typeof value !== "string" || value.length < min || value.length > max) {
    throw new InvalidBackupError(`${path} must be a string between ${min} and ${max} characters`);
  }
  return value;
}

function requireInteger(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || (value as number) < min || (value as number) > max) {
    throw new InvalidBackupError(`${path} must be an integer between ${min} and ${max}`);
  }
  return value as number;
}

function requireFiniteNumber(value: unknown, path: string, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) {
    throw new InvalidBackupError(`${path} must be a finite number between ${min} and ${max}`);
  }
  return value;
}

function requireLocalDate(value: unknown, path: string): string {
  const date = requireString(value, path, 10, 10);
  const match = LOCAL_DATE.exec(date);
  if (!match) throw new InvalidBackupError(`${path} must be a valid local date`);
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const day = Number(date.slice(8, 10));
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
    throw new InvalidBackupError(`${path} must be a valid local date`);
  }
  return date;
}

function requireTimestamp(value: unknown, path: string): string {
  const timestamp = requireString(value, path, 20, 40);
  if (Number.isNaN(Date.parse(timestamp))) throw new InvalidBackupError(`${path} must be a timestamp`);
  return timestamp;
}

function requireEqual(value: unknown, expected: unknown, path: string): void {
  if (value !== expected) throw new InvalidBackupError(`${path} is not supported`);
}

function constantTimeEqual(left: string, right: string): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= left.charCodeAt(index) ^ right.charCodeAt(index);
  }
  return difference === 0;
}
